/** @jsx jsx */
import { Flex, Paragraph, Stack } from "@eworkit/iris";
import React, { FC, memo, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";
import sanitize from "sanitize-html";
import { jsx } from "theme-ui";

import { useAnalytics } from "@/components/analytics/useAnalytics";
import { Button } from "@/components/Button";
import { IFetcherRenderProps } from "@/components/Fetcher";
import { MetaTags } from "@/components/MetaTags";
import { ResponsiveCard } from "@/components/ResponsiveCard";
import { CardSection } from "@/components/Section";
import { ShareButton, ShareIcons, TShareTarget } from "@/components/Share";
import { Text } from "@/components/Text";
import { UserContext } from "@/components/User";
import { MEDIA_SIZE_SM } from "@/core/media";
import { compose } from "@/core/url";
import {
  API,
  deleteFavouriteJobRequest,
  getJobRequestClientInfo,
  IJobRequest,
  setFavouriteJobRequest,
} from "@/data/job-requests";
import { useJobRequestApplyAvailability } from "@/data/job-requests/hooks/useJobRequestApplyAvailability";
import { formatMultipleLocations } from "@/data/locations";
import { getVariable, Variables } from "@/env";
import { Col, Flex as OldFlex } from "@/eworkitComponents";
import { SessionContext } from "@/infrastructure/Session";
import { PublicProfileCard } from "@/views/Clients/PublicProfileCard";
import { DesignatedCandidate } from "@/views/components/JobRequests/Partials";
import { LocationWidget, RecentlyViewedWidget, SimilarJobsWidget } from "@/views/components/JobRequests/Widgets";
import { withHttpError } from "@/views/Errors";
import { router } from "@/views/router";

import { JobRequestApplications } from "../components/JobApplications/JobRequestApplications";
import { JobRequestMatchingConsultant } from "./components/JobRequestMatchingConsultant";
import { JobRequestDetailMainSection } from "./JobRequestDetailMainSection";

const APP_API = getVariable(Variables.APP_API) || "";

interface IJobRequestDetailRendererProps extends IFetcherRenderProps<IJobRequest> {}

export const JobRequestDetailRendererComponent: FC<IJobRequestDetailRendererProps> = memo(
  ({ payload, updatePayload }) => {
    const { t } = useTranslation("session");
    const { authenticated } = useContext(SessionContext);
    const { sendEvent } = useAnalytics();

    const User = useContext(UserContext);
    const companyId = User.contextCompany?.id;

    const { isApplyAvailable } = useJobRequestApplyAvailability(payload);

    const handleFavouritesButtonClick = useCallback(async () => {
      if (!payload) {
        return;
      }

      try {
        if (payload.favourite) {
          await deleteFavouriteJobRequest({ jobRequestId: payload.id, companyId });
        } else {
          await setFavouriteJobRequest({ jobRequestId: payload.id, companyId });

          sendEvent({
            type: "save_job",
            id: payload.id,
            name: payload.title!,
            location: formatMultipleLocations(t, payload.locations, payload.locations?.length),
          });
        }

        updatePayload({ ...payload, favourite: !payload.favourite });
      } catch (error) {
        // TODO: handle errors
      }
    }, [payload, companyId, sendEvent, t, updatePayload]);

    const handleShared = useCallback(
      (method: TShareTarget | "link") => {
        if (!payload) {
          return;
        }

        sendEvent({
          type: "share_job",
          id: payload.id,
          name: payload.title!,
          location: formatMultipleLocations(t, payload.locations, payload.locations?.length),
          method,
        });
      },
      [payload, sendEvent, t],
    );

    const isSmallScreen = useMedia(MEDIA_SIZE_SM);

    if (!payload) {
      return null;
    }

    const titleTrunc = payload.title
      ? `${payload.title.substring(0, 35).trim()}${payload.title.length > 35 ? "..." : ""}`
      : "";
    const descriptionSanitized = payload.description
      ? sanitize(payload.description, { allowedTags: [], allowedAttributes: {} })
      : "";
    const descriptionTrunc = `${descriptionSanitized.substring(0, 70).trim()}${
      descriptionSanitized.length > 70 ? "..." : ""
    }`;

    const publicUrl =
      window.location.origin + router.get("public-job-requests.detail", { jobRequestId: payload.id }).path;

    const shareAndSaveActionsClass = "share-and-save-actions";
    const shareAndSaveActionsPublicClass = "share-and-save-public";

    const clientInfo = getJobRequestClientInfo(payload);

    const isLocationAvailable = Boolean(payload.locations?.length);

    return (
      <React.Fragment>
        <MetaTags
          title={titleTrunc}
          description={descriptionTrunc}
          ogImage={`${APP_API}${compose(API.ogImage, { jobRequestId: payload.id })}`}
          ogImageAlt={t("META.JOB_REQUEST.IMAGE_DESCRIPTION")}
        />

        <Col width={{ lg: 8, xxs: 12 }}>
          <OldFlex width={12} direction="column" innerMargin="md">
            {!!payload.designatedCandidate && <DesignatedCandidate designatedCandidate={payload.designatedCandidate} />}

            <JobRequestDetailMainSection key={payload.id} payload={payload} />

            {clientInfo.legalEntity?.publicProfileSlug && <PublicProfileCard legalEntity={clientInfo.legalEntity} />}
          </OldFlex>
        </Col>

        <Col width={{ lg: 4, xxs: 12 }}>
          {authenticated && (
            <OldFlex direction="row">
              {payload.public && (
                <CardSection
                  className={authenticated ? shareAndSaveActionsClass : shareAndSaveActionsPublicClass}
                  margin={[false, false, "md", false]}
                  width={12}
                  align="center">
                  <ShareButton
                    variant="vertical-icon-tab"
                    size="lg"
                    url={publicUrl}
                    showLabel={!isSmallScreen}
                    icon="share"
                    onShared={handleShared}
                  />
                </CardSection>
              )}

              <CardSection
                className={shareAndSaveActionsClass}
                margin={[false, false, "md", false]}
                width={12}
                align="center">
                <Button
                  variant="vertical-icon-tab"
                  size="lg"
                  icon="bookmark"
                  iconColor={payload.favourite ? "default" : "inherit"}
                  onClick={handleFavouritesButtonClick}>
                  {!isSmallScreen && (
                    <Text size="lg">
                      {t(payload.favourite ? "JOB_REQUESTS.DETAIL.SAVED_JOB" : "JOB_REQUESTS.DETAIL.SAVE_JOB")}
                    </Text>
                  )}
                </Button>
              </CardSection>
            </OldFlex>
          )}

          <Stack gap="sm">
            {!authenticated && payload.public && (
              <ResponsiveCard sx={{ p: "md" }}>
                <Flex justifyContent="space-between" alignItems="center">
                  <Paragraph size="p-lg" color="n700">
                    {t("JOB_REQUESTS.DETAIL.SHARE_THIS_JOB")}
                  </Paragraph>
                  <ShareIcons url={publicUrl} onShared={handleShared} />
                </Flex>
              </ResponsiveCard>
            )}

            {isLocationAvailable && <LocationWidget key={payload.id} record={payload} />}

            {authenticated && isApplyAvailable && <JobRequestMatchingConsultant jobRequest={payload} />}

            <SimilarJobsWidget jobRequestId={payload.id} isPublic />

            {authenticated && (
              <React.Fragment>
                <RecentlyViewedWidget />
                <JobRequestApplications jobRequest={payload} />
              </React.Fragment>
            )}
          </Stack>
        </Col>
      </React.Fragment>
    );
  },
);

export const PublicJobRequestDetail = withHttpError(JobRequestDetailRendererComponent);
